import React, { FC, useCallback, useState } from 'react';
import styled from 'styled-components';
import { useTenantActions } from 'utils/hooks/useTenantConfig';
import { Button, Switch, Typography, useEffectOnce } from '@beeline.com/bp-design-system';
import { MultiEngagementSettings } from './MultiEngagement.types';
import { mergeMultiEngagementSettings } from './mergeMultiEngagementSettings';
import { useMultiEngagementSelector } from './useMultiEngagementSelector';
import { useMultiEngagementIntl } from './translations/useMultiEngagementIntl';
import { Counter } from '@utmost.co/utmost-design-system/src/components';

export const MultiEngagement: FC<React.PropsWithChildren<unknown>> = () => {
  const intl = useMultiEngagementIntl();
  const [settings, setSettings] = useState<MultiEngagementSettings>();
  const { updateFeature } = useTenantActions();
  const initialSettings = useMultiEngagementSelector();
  const [hasChanged, setHasChanged] = useState(false);

  useEffectOnce(() => {
    initialiseSettings();
  });

  const initialiseSettings = useCallback(() => {
    const employee = initialSettings.maxActiveEmployeeEngagements;
    const nonEmployee = initialSettings.maxActiveNonEmployeeEngagements;

    if (initialSettings.engagement && employee && nonEmployee) {
      setSettings({
        maxActiveEmployeeEngagements: employee?.enabled,
        maxActiveEmployeeEngagementsNumber: parseInt(
          employee?.config['max-active-employee-engagements-number'] || '1',
          10
        ),
        maxActiveNonEmployeeEngagements: nonEmployee?.enabled,
        maxActiveNonEmployeeEngagementsNumber: parseInt(
          nonEmployee?.config['max-active-non-employee-engagements-number'] || '1',
          10
        )
      });
    }
  }, [initialSettings]);

  const handleSubmit = useCallback(async () => {
    if (settings && initialSettings.engagement) {
      await updateFeature('engagement', mergeMultiEngagementSettings(settings, initialSettings.engagement), true);
    }
  }, [settings, initialSettings.engagement, updateFeature]);

  const handleCancel = useCallback(() => {
    initialiseSettings();
    setHasChanged(false);
  }, [initialiseSettings]);

  const handleUpdateSettings = useCallback((updateSettings: Partial<MultiEngagementSettings>) => {
    setSettings((previousSettings) => {
      if (!previousSettings) {
        return previousSettings;
      }

      const hasDiff = (
        Object.entries(updateSettings) as Array<
          [keyof MultiEngagementSettings, MultiEngagementSettings[keyof MultiEngagementSettings]]
        >
      ).some(([key, value]) => previousSettings[key] !== value);

      if (!hasDiff) {
        return previousSettings;
      }

      setHasChanged(true);
      return { ...previousSettings, ...updateSettings };
    });
  }, []);

  if (!settings) return <div data-test="multi-engagement-empty-container" />;

  return (
    <div data-test="multi-engagement-container">
      <StyledSection>
        <StyledBlock>
          <Typography variant="h5">{intl.multiEngagementSettings}</Typography>
          <Typography variant="body1">{intl.enableMultiEngagementSettings}</Typography>
        </StyledBlock>

        <StyledMultiSettings>
          <StyledOption>
            <Typography variant="label1">{intl.multiEngagementsForEmployees}</Typography>
            <StyledSwitchContainer>
              <Switch
                data-test="max-active-employee-engagements"
                checked={settings.maxActiveEmployeeEngagements}
                onChange={(value) => handleUpdateSettings({ maxActiveEmployeeEngagements: value.target.checked })}
              />
              <StyledTypography variant="label1">
                {settings?.maxActiveEmployeeEngagements ? intl.enabled : intl.disabled} {`(${intl.locked})`}
              </StyledTypography>
            </StyledSwitchContainer>
          </StyledOption>
          <StyledOption $disabled={!settings.maxActiveEmployeeEngagements}>
            <Typography variant="label1">{intl.concurrentActiveEngagementsForEmployees}</Typography>
            <Typography variant="body2">{intl.setMaxConcurrentActiveEngagementsForEmployees}</Typography>
            <StyledCounter
              dataTest="max-active-employee-engagements-number"
              initialValue={settings.maxActiveEmployeeEngagementsNumber}
              min={1}
              disabled={!settings.maxActiveEmployeeEngagements}
              onChange={(value) => handleUpdateSettings({ maxActiveEmployeeEngagementsNumber: value })}
            />
          </StyledOption>
          <StyledOption>
            <Typography variant="label1">{intl.multiEngagementsForNonEmployees}</Typography>
            <StyledSwitchContainer>
              <Switch
                data-test="max-active-non-employee-engagements"
                checked={settings.maxActiveNonEmployeeEngagements}
                onChange={(value) => handleUpdateSettings({ maxActiveNonEmployeeEngagements: value.target.checked })}
              />
              <StyledTypography variant="label1">
                {settings.maxActiveNonEmployeeEngagements ? intl.enabled : intl.disabled} {`(${intl.locked})`}
              </StyledTypography>
            </StyledSwitchContainer>
          </StyledOption>
          <StyledOption $disabled={!settings.maxActiveNonEmployeeEngagements}>
            <Typography variant="label1">{intl.concurrentActiveEngagementsForNonEmployees}</Typography>
            <Typography variant="body2">{intl.setMaxConcurrentActiveEngagementsForNonEmployees}</Typography>
            <StyledCounter
              dataTest="max-active-non-employee-engagements-number"
              initialValue={settings.maxActiveNonEmployeeEngagementsNumber}
              min={1}
              disabled={!settings.maxActiveNonEmployeeEngagements}
              onChange={(value) => handleUpdateSettings({ maxActiveNonEmployeeEngagementsNumber: value })}
            />
          </StyledOption>
        </StyledMultiSettings>
      </StyledSection>

      <StyledSection>
        <StyledCancelButton
          dataTest="cancel-engagement-settings"
          disabled={!hasChanged}
          onClick={handleCancel}
          variant="text"
        >
          {intl.cancel}
        </StyledCancelButton>
        <Button
          dataTest="save-engagement-settings"
          disabled={!hasChanged}
          onClick={handleSubmit}
          variant="contained"
          color="primary"
        >
          {intl.save}
        </Button>
      </StyledSection>
    </div>
  );
};

const StyledSwitchContainer = styled.div`
  padding-top: ${({ theme }) => theme.spacingSize.m};
`;

const StyledTypography = styled(Typography)`
  && {
    display: inline-block;
    margin-left: ${({ theme }) => theme.spacingSize.m};
  }
`;

const StyledMultiSettings = styled.div`
  gap: ${({ theme }) => theme.spacingSize.xxl};
  display: grid;
  grid-template-columns: auto 1fr;
`;

const StyledOption = styled.div<{
  $disabled?: boolean;
}>`
  padding-right: ${({ theme }) => theme.spacingSize.xxl};
  .label1,
  .body2 {
    color: ${({ $disabled, theme }) => ($disabled ? theme.colorOnSurfaceVariant : 'inherit')};
  }
`;

const StyledSection = styled.div`
  padding-top: ${({ theme }) => theme.spacingSize.xxl};

  width: 100%;
`;

const StyledBlock = styled.div`
  padding-bottom: ${({ theme }) => theme.spacingSize.xl};
  width: 100%;
`;

const StyledCounter = styled(Counter)`
  margin-top: ${({ theme }) => theme.spacingSize.l};
`;

const StyledCancelButton = styled(Button)`
  && {
    margin-right: ${({ theme }) => theme.spacingSize.l};
  }
`;
